import { useLazyGetProjectByBarcodeQuery } from 'app/services/projects';
import { Paths, User } from 'constants/index';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import Scanner from 'components/Scanner/Scanner';
import { useEffect, useState } from 'react';
import { Skeleton } from 'antd';
import { useLazyGetToolOverviewByBarcodeQuery } from 'app/services/tools';
import { useHasPermission } from 'hooks/authorization/useHasPermission';
import { BarcodeType, parseQrValue } from 'utils/qr/parseQrValue';

import './scan.less';

const Scan = () => {
  const { t } = useTranslation();
  const canScanWorkplace = useHasPermission(User.Permissions.ACCESS_PROJECT_WITH_BARCODE);

  const [ getProject, { data : projectData, isFetching, isLoading, isError, isSuccess } ] = useLazyGetProjectByBarcodeQuery();
  const [ getToolOverview, { data: toolOverview, isFetching: isToolFetching, isLoading: isToolLoading, isError: isToolError } ] = useLazyGetToolOverviewByBarcodeQuery();

  const [ scanned, setScanned ] = useState<string>();
  const handler = async (result: string) => setScanned(result);

  useEffect(() => {
    if (!scanned) return;

    const { barcodeValue, type } = parseQrValue(scanned);

    if (type === BarcodeType.DEVICE) return;

    if (type === BarcodeType.TOOL) {
      getToolOverview(barcodeValue);
      return;
    }

    if (canScanWorkplace) getProject(barcodeValue);
  }, [ scanned ]);

  if (scanned && projectData) {
    const url = Paths.PROJECTS_VIEW.replace(':siteId', projectData.SiteId.toString()).replace(':id', projectData.Id.toString());
    return <Navigate to={url} />;
  }

  const parsed = scanned ? parseQrValue(scanned) : undefined;
  const isToolScan = parsed?.type === BarcodeType.TOOL;

  // Same page a phone camera opens: a tool goes to its overview, a logger to its verification code.
  if (parsed?.type === BarcodeType.DEVICE) {
    return <Navigate to={Paths.DEVICES_OVERVIEW.replace(':barcode', encodeURIComponent(parsed.barcodeValue))} />;
  }

  if (scanned && toolOverview?.Found && parsed) {
    const url = toolOverview.ToolId
      ? Paths.TOOLS_VIEW.replace(':id', toolOverview.ToolId.toString())
      : Paths.TOOLS_OVERVIEW.replace(':barcode', encodeURIComponent(parsed.barcodeValue));
    return <Navigate to={url} />;
  }

  // Only the query that actually ran for this scan type determines a real not-found:
  // legacy codes only ever call getProject, tool URLs only ever call getToolOverview.
  // No workplace for the code (unknown, or not the user's own unit) is a not-found like a tool or logger overview.
  const noWorkplace = isError || !canScanWorkplace || (isSuccess && !projectData);
  if (scanned && (isToolScan ? isToolError : noWorkplace)) return <Navigate to={Paths.NOT_FOUND} />;

  return (
    <>
      <Helmet title={`Kvalilog > ${t('pages.scan')}`} />
      {!scanned && <div className="scan-page"><Scanner callback={handler} /></div>}
      {scanned && !projectData && (isFetching || isToolFetching || isToolLoading || isLoading) && <Skeleton active />}
    </>
  );
};

export default Scan;
